import React, { useEffect, useState } from "react";
import { Card, Table, Button, Row, Col, Form } from "react-bootstrap";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const BASE_URL = "https://sutmmg.codydeasoftech.in/api";

const UlbWiseMeasurementCountReport = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [district, setDistricts] = useState([]);
  const [municipalityList, setMunicipalities] = useState([]);
  const [filters, setFilters] = useState({
    municipality_id: "",
    district_id: "",
  });

  const navigate = useNavigate();

  const getAuthHeader = () => ({
    headers: {
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  });

  const handleExportExcel = () => {
    if (!reports.length) {
      Swal.fire("No Data", "Nothing to export", "warning");
      return;
    }

    // Format data for Excel
    const formattedData = reports.map((r, index) => ({
      "SL No": index + 1,
      "District": r.district_name || "-",
      "ULB Name": r.municipality_name || "-",
      "Total Schools": r.total_school_count || 0,
      "Total Class": r.total_class_count || 0,
      "Total Students": r.total_student_count || 0,
      "Measurement Entered (Partial & Full)":
        r.measurements_taken_students_count || 0,
      "SHG Members Added": r.shg_members_added || 0,
    }));

    // Create worksheet
    const worksheet = XLSX.utils.json_to_sheet(formattedData);

    // Create workbook
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "ULB Report");

    // Generate Excel file
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const data = new Blob([excelBuffer], {
      type:
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
    });

    saveAs(data, `ULB_Measurement_Report_${Date.now()}.xlsx`);
  };

  const loadDistricts = async () => {
    try {
      const res = await axios.post(`${BASE_URL}/districtMasterList`,
        {}, getAuthHeader());
      setDistricts(res.data?.data || []);
    } catch (err) {
      console.error("District load error:", err);
      if (err.response?.status === 401) {
        Swal.fire("Session Expired", "Please login again", "warning");
        localStorage.clear();
        navigate("/suda/login");
      } else if (err.response?.status === 422) {
        Swal.fire(
          "Validation Failed",
          err.response.data?.message || "Invalid request",
          "warning"
        );
      } else {
        Swal.fire("Alert!", "Failed to load districts", "error");
      }
    }
  };

  const loadMunicipalities = async (districtId = filters.district_id) => {
    try {
      const res = await axios.post(
        `${BASE_URL}/municipalityList`,
        {
          district_id: districtId || "",
        },
        getAuthHeader()
      );
      setMunicipalities(res.data?.data || []);
    } catch (err) {
      if (err.response?.status === 401) {
        Swal.fire("Session Expired", "Please login again", "warning");
        localStorage.clear();
        navigate("/suda/login");
      } else if (err.response?.status === 422) {
        Swal.fire(
          "Validation Failed",
          err.response.data?.message || "Invalid request",
          "warning"
        );
      } else {
        Swal.fire("Alert!", "Failed to load municipalities", "error");
      }
    }
  };

  const loadReports = async (customFilters = filters) => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");

      const res = await axios.post(
        `${BASE_URL}/municipalityWiseMeasurementCountReport`,
        customFilters,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      // Sort the reports alphabetically by district_name
      const sortedReports = (res.data.data || []).sort((a, b) => {
        const districtA = (a.district_name || "").toLowerCase();
        const districtB = (b.district_name || "").toLowerCase();
        if (districtA < districtB) return -1;
        if (districtA > districtB) return 1;
        return 0;
      });

      setReports(sortedReports);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDistricts();
    loadReports();
  }, []);

  const theadStyle = {
    padding: "8px",
    fontWeight: "600",
    borderBottom: "1px solid black",
    borderTop: "1px solid black",
    textAlign: "center",
    backgroundColor: "#edf7fe",
  };

  return (
    <div className="container mt-4" style={{ maxWidth: 1600 }}>
      {/* ================= FILTER CARD ================= */}
      <Card
        className="mb-4 border-0"
        style={{
          background: "linear-gradient(135deg, #E3F2FD, #ffffff)",
          borderRadius: "14px",
          boxShadow: "0 8px 25px rgba(21,101,193,0.15)",
        }}
      >
        <div
          className="px-4 py-3 text-white rounded-top"
          style={{
            background: "linear-gradient(90deg, #0D47A1, #1565C1)",
          }}
        >
          <h5 className="mb-0 fw-bold">
            ULB Wise Measurement Count & SHG Count Report
          </h5>
        </div>

        <Card.Body className="px-4 py-4">
          <Row className="align-items-center">
            {/* District */}
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold text-dark">District</Form.Label>
                <Form.Select
                  value={filters.district_id}
                  onChange={(e) => {
                    const districtId = e.target.value;
                    setFilters({
                      ...filters,
                      district_id: districtId,
                      municipality_id: "",
                    });
                    loadMunicipalities(districtId);
                  }}
                  style={{
                    borderRadius: "10px",
                    border: "2px solid #1565C1",
                  }}
                >
                  <option value="">Select District</option>
                  {district.map((d) => (
                    <option value={d.id} key={d.id}>
                      {d.name || d.district_name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            {/* Municipality */}
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">
                  Municipality
                </Form.Label>
                <Form.Select
                  value={filters.municipality_id}
                  style={{ borderRadius: "10px", border: "2px solid #1565C1" }}
                  onChange={(e) =>
                    setFilters({ ...filters, municipality_id: e.target.value })
                  }
                >
                  <option value="">All Municipalities</option>
                  {municipalityList.map((m) => (
                    <option key={m.user_id} value={m.user_id}>
                      {m.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            {/* Buttons */}
            <Col md={4} className="d-flex gap-3 mt-3">
              {/* SEARCH */}
              <Button
                className="w-50 fw-semibold text-white"
                style={{
                  background: "#1565C1",
                  border: "none",
                  borderRadius: "10px",
                  transition: "0.3s",
                }}
                onMouseEnter={(e) => (e.target.style.background = "#0D47A1")}
                onMouseLeave={(e) => (e.target.style.background = "#1565C1")}
                onClick={() => {
                  loadReports();
                }}
              >
                <i className="fa-solid fa-magnifying-glass me-2"></i> Search
              </Button>

              {/* RESET */}
              <Button
                className="w-50 fw-semibold text-white"
                style={{
                  background: "#E53935",
                  border: "none",
                  borderRadius: "10px",
                  transition: "0.3s",
                }}
                onMouseEnter={(e) => (e.target.style.background = "#D32F2F")}
                onMouseLeave={(e) => (e.target.style.background = "#E53935")}
                onClick={() => {
                  const resetFilters = { municipality_id: "", district_id: "" };
                  setFilters(resetFilters);
                  setMunicipalities([]);
                  loadReports(resetFilters);
                }}
              >
                <i className="fa-solid fa-arrow-rotate-left me-2"></i> Reset
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Button
        style={{
          background: "#43A047",
          border: "none",
          borderRadius: "8px",
        }}
        className="mb-4"
        onClick={handleExportExcel}
      >
        <i className="fa-solid fa-file-excel"></i> Export to Excel
      </Button>

      {/* ================= TABLE ================= */}
      <Card
        className="border-0"
        style={{
          boxShadow: "0 10px 35px rgba(21,101,193,0.18)",
          overflow: "hidden",
        }}
      >
        {loading ? (
          <div className="loader-wrapper">
            <div className="blue-spinner"></div>
            <div className="loading-line">
              <div className="loading-progress"></div>
            </div>
            <p className="loading-text">Please Wait... While Gathering Reports</p>
          </div>
        ) : (
          <Table bordered hover responsive striped className="mb-0">
            <thead>
              <tr>
                <th style={theadStyle}>SL No</th>
                <th style={theadStyle}>District</th>
                <th style={theadStyle}>ULB Name</th>
                <th style={theadStyle}>Total Schools</th>
                <th style={theadStyle}>Total Class</th>
                <th style={theadStyle}>Total Students</th>
                <th style={theadStyle}>Number of Measurement Entered (Partial & Full)</th>
                <th style={theadStyle}>SHG Members Assigned</th>
              </tr>
            </thead>
            <tbody>
              {reports.length ? (
                reports.map((r, index) => (
                  <tr key={index}>
                    <td className="text-center">{index + 1}</td>
                    <td className="text-center">{r.district_name || "-"}</td>
                    <td className="text-center">{r.municipality_name || "-"}</td>
                    <td className="text-center">{r.total_school_count || 0}</td>
                    <td className="text-center">{r.total_class_count || 0}</td>
                    <td className="text-center">{r.total_student_count || 0}</td>
                    <td className="text-center">{r.measurements_taken_students_count || 0}</td>
                    <td className="text-center">{r.shg_members_added || 0}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="text-center py-4 text-muted">
                    No records found
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        )}
      </Card>
    </div>
  );
};

export default UlbWiseMeasurementCountReport;
